import { Link } from "@mui/material";

import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import {
  faqs,
  howItWorks,
  purchaseSeatsLanguages,
  AugustaGreenJacketsLink,
} from "./helpers";
import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";

import FindGamesBannerImage from "public/images/augustagreenjackets_flex/AGJ-Attend-Mini-Plan.jpg";
import StaticSeatMap from "public/images/augustagreenjackets_flex/GreenJacketsStadium.png";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const config: TeamConfig = {
  teamSchema: "augustagreenjackets_flex",
  browserTitle: "7-Game Pack",
  packages: true,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "", // Optional, set to empty string
  teamName: "Augusta GreenJackets",

  // Seat map configs
  seatMapConfigs: {
    venueId: "", // Not available
    mapId: "", // Not available
    staticSeatMapImage: StaticSeatMap.src, // Use the static seat map image
    isShowAvailableSeatsButtonHidden: true,
    isView3dButtonHidden: true,
    customSeatMapStaticImageStyle: {
      width: "70%",
      display: "block",
      marginX: "auto",
    },
  },

  // Gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: "",
    redeemSuccessModalLink: "",
    hideRedeemItem: false,
    customShareGiftUrl: "",
    customErrorMessage: "",
    hideRecipient: false,
  },

  // Purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // Disabled routes
  disabledRoutes: [routes.giftCard],

  // Switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // Purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 21,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: true,
    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 1,
    checkoutTimerEnabled: false,
    isDisabledEventSelection: false,
    hideAllUpcomingGames: false,
    isRepProduct: false,
  },

  // Feature configs
  packageConfigs: {
    isPackageEnabled: true,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // Intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // Checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity, // No limit specified
    allowedPaymentFailCount: Infinity, // No limit specified
  },

  isSaveCardAllowed: true,
  isBrainTreePaymentEnabled: false,
  shouldUseInternalLogin: false,
  oktaClientId: "0oasmcdgh92Jom7TI357",
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  platform: vendorUUIDs.tickesdotcom, // TDC platform
  refreshInventoryWhenEventClick: false,

  // Analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
  },

  // Redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // URLs
  accountManagerUrl:
    "https://mlb.tickets.com/?orgid=56772&agency=AGRM_MYTIXX#/auth/login?fullret=%2Fticketmanagement%2F%3Forgid%3D56772%26agency%3DAGRM_MYTIXX",

  // Images
  images: {
    findGamesBannerImage: FindGamesBannerImage.src, // Replace with actual image source
    authBackgroundImage: "",
    checkoutStatusPageImage: "",
    logoImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/augusta-green-jackets.png" +
      "?width=128&height=75", // actual size 380x194,
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/augusta-green-jackets.png",
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },
    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },
    progressBar: {
      staticProgressBarText: false,
    },
    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="tel:8033499402"
        >
          (803) 349-9402
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#007754",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: purchaseSeatsLanguages,
    // Gift card languages are empty since gift cards are disabled
    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,
    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: (
      <>
        Your Augusta GreenJackets tickets are confirmed! Your tickets will be
        available GreenJackets <AugustaGreenJacketsLink /> Account.
      </>
    ),
    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
